/**
 * AdminInvoices.jsx - AR Cockpit & Invoice List (Standardized)
 * 
 * Refactored to use standard admin list components:
 * - AdminListHeader with stats cards
 * - AdminDataTable with ColumnSelector
 * - useAdminTable pattern for data management
 * - FilterBar with status chips
 * 
 * Features:
 * - Adjustable columns (add/remove/reorder)
 * - Order # column (links to Order Detail)
 * - Stats cards in header
 * - Due Date filter as primary
 * - Global search
 * - Export CSV
 * - Send Reminder
 * - Record Payment
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FileText, DollarSign, AlertTriangle, Clock, Eye, CreditCard, 
  Mail, Download, Search, X, Calendar, Building2, ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import AdminDataTable, { ColumnSelector } from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import { arInvoicesAPI, crmAPI } from '../../utils/api';
import { toast } from 'sonner';
import CreatePaymentModal from '../../components/admin/CreatePaymentModal';

// ===================================================
// CONSTANTS
// ===================================================

const STORAGE_KEY = 'admin_invoices_table_prefs_v1';

const STATUS_OPTIONS = [
  { value: 'SENT', label: 'Sent (Open)', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'OVERDUE', label: 'Overdue', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'ISSUED', label: 'Issued', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'PAID', label: 'Paid', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'VOID', label: 'Void', color: 'bg-gray-100 text-gray-600 border-gray-300' },
  { value: 'DRAFT', label: 'Draft', color: 'bg-gray-50 text-gray-500 border-gray-200' },
];

const STATUS_COLOR_MAP = STATUS_OPTIONS.reduce((acc, s) => {
  acc[s.value] = s.color;
  return acc;
}, {});

const DEFAULT_VISIBLE_COLUMNS = {
  invoice_number: true,
  order_number: true,
  account_name: true,
  issued_at: true,
  due_at: true,
  status: true,
  days_overdue: true,
  total: true,
  balance_due_cents: true,
  amount_paid_cents: false,
  account_id: false,
};

const DEFAULT_COLUMN_ORDER = [
  'invoice_number',
  'order_number',
  'account_name',
  'issued_at',
  'due_at',
  'status',
  'days_overdue',
  'total',
  'balance_due_cents',
  'amount_paid_cents',
  'account_id',
];

// ===================================================
// HELPER FUNCTIONS
// ===================================================

const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  const value = typeof cents === 'number' ? cents / 100 : cents;
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

// ===================================================
// STATUS BADGE COMPONENT
// ===================================================

const StatusBadge = ({ status, isOverdue, daysOverdue }) => {
  const displayStatus = isOverdue ? 'OVERDUE' : (status || 'SENT').toUpperCase();
  const colorClass = STATUS_COLOR_MAP[displayStatus] || 'bg-gray-100 text-gray-600';
  
  return (
    <div className="flex items-center gap-2">
      <Badge className={`${colorClass} font-medium text-xs`}>
        {displayStatus}
      </Badge>
      {isOverdue && daysOverdue > 0 && (
        <Badge variant="outline" className="text-red-600 border-red-300 text-xs">
          {daysOverdue}d
        </Badge>
      )}
    </div>
  );
};

// ===================================================
// DAYS OVERDUE DISPLAY
// ===================================================

const DaysOverdueDisplay = ({ daysOverdue, isOverdue }) => {
  if (!isOverdue || daysOverdue <= 0) return <span className="text-gray-400">—</span>;
  
  const severity = daysOverdue > 30 ? 'text-red-600 font-semibold' : 
                   daysOverdue > 14 ? 'text-orange-600' : 'text-yellow-600';
  
  return (
    <span className={severity}>
      {daysOverdue} days
    </span>
  );
};

// ===================================================
// STATS CARDS COMPONENT
// ===================================================

const StatsCards = ({ stats, loading }) => {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="py-3 px-4">
              <div className="h-3 bg-gray-200 rounded w-16 mb-2" />
              <div className="h-6 bg-gray-200 rounded w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const globalStats = stats.global || {};
  
  const cards = [
    { 
      label: 'Total AR', 
      value: formatCurrency(globalStats.total_ar_cents || 0),
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 border-blue-100',
    },
    { 
      label: 'Overdue AR', 
      value: formatCurrency(globalStats.overdue_ar_cents || 0),
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50 border-red-100',
    },
    { 
      label: 'Overdue Count', 
      value: globalStats.overdue_count || 0,
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 border-amber-100',
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-4">
      {cards.map((card, i) => (
        <Card key={i} className={`${card.bgColor} border`}>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              <card.icon className={`w-3.5 h-3.5 ${card.color}`} />
              {card.label}
            </div>
            <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ===================================================
// MAIN COMPONENT
// ===================================================

const AdminInvoices = ({ onViewOrder }) => {
  // ===== STATE =====
  
  // Data
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Sorting
  const [sortBy, setSortBy] = useState('due_at');
  const [sortDir, setSortDir] = useState('asc');
  
  // Filters
  const [selectedStatuses, setSelectedStatuses] = useState(['SENT']);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [dueDateFrom, setDueDateFrom] = useState('');
  const [dueDateTo, setDueDateTo] = useState('');
  const [issuedDateFrom, setIssuedDateFrom] = useState('');
  const [issuedDateTo, setIssuedDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Table preferences
  const [density, setDensity] = useState('comfortable');
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
  
  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // ===== LOAD PREFERENCES =====
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const prefs = JSON.parse(saved);
        if (prefs.visibleColumns) setVisibleColumns(prefs.visibleColumns);
        if (prefs.columnOrder) setColumnOrder(prefs.columnOrder);
        if (prefs.density) setDensity(prefs.density);
        if (prefs.pageSize) setPageSize(prefs.pageSize);
      }
    } catch (e) {
      console.warn('Failed to load preferences:', e);
    }
  }, []);

  // ===== SAVE PREFERENCES =====
  
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        visibleColumns,
        columnOrder,
        density,
        pageSize,
      }));
    } catch (e) {
      console.warn('Failed to save preferences:', e);
    }
  }, [visibleColumns, columnOrder, density, pageSize]);

  // ===== FETCH DATA =====
  
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        account_id: filterCustomer || undefined,
        due_from: dueDateFrom || undefined,
        due_to: dueDateTo || undefined,
        issued_from: issuedDateFrom || undefined,
        issued_to: issuedDateTo || undefined,
        search: searchQuery || undefined,
      };
      
      const data = await arInvoicesAPI.list(params);
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortDir, selectedStatuses, filterCustomer, 
      dueDateFrom, dueDateTo, issuedDateFrom, issuedDateTo, searchQuery]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = {
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        account_id: filterCustomer || undefined,
        due_from: dueDateFrom || undefined,
        due_to: dueDateTo || undefined,
        issued_from: issuedDateFrom || undefined,
        issued_to: issuedDateTo || undefined,
      };
      const data = await arInvoicesAPI.getStats(params);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [selectedStatuses, filterCustomer, dueDateFrom, dueDateTo, issuedDateFrom, issuedDateTo]);

  const fetchCustomers = useCallback(async () => {
    try {
      const data = await crmAPI.getAccounts({ page_size: 250 });
      setCustomers(data.accounts || []);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // ===== HANDLERS =====
  
  const handleRefresh = useCallback(() => {
    fetchInvoices();
    fetchStats();
  }, [fetchInvoices, fetchStats]);

  const handleSort = useCallback((field) => {
    if (sortBy === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  }, [sortBy]);

  const handleRowClick = useCallback((row) => {
    if (onViewOrder && row.order_id) {
      onViewOrder(row.order_id);
    }
  }, [onViewOrder]);

  const handleRecordPayment = useCallback((invoice, e) => {
    e?.stopPropagation();
    setSelectedInvoice({
      id: invoice.id,
      invoice_number: invoice.invoice_number,
      order_id: invoice.order_id,
      account_id: invoice.account_id,
      account_name: invoice.account_name,
      total_cents: invoice.total_cents || Math.round((invoice.total || 0) * 100),
      balance_cents: invoice.balance_due_cents,
    });
    setShowPaymentModal(true);
  }, []);

  const handleSendReminder = useCallback(async (invoice, e) => {
    e?.stopPropagation();
    if (!invoice.id) {
      toast.error('Invoice ID not found');
      return;
    }
    try {
      toast.info(`Sending reminder for ${invoice.invoice_number}...`);
      const result = await arInvoicesAPI.sendReminder(invoice.id);
      toast.success(result.message || 'Reminder sent!');
    } catch (err) {
      toast.error(err.message || 'Failed to send reminder');
    }
  }, []);

  const handleExportCsv = useCallback(async () => {
    try {
      toast.info('Exporting invoices...');
      const blob = await arInvoicesAPI.exportCsv({
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        account_id: filterCustomer || undefined,
        due_from: dueDateFrom || undefined,
        due_to: dueDateTo || undefined,
        issued_from: issuedDateFrom || undefined,
        issued_to: issuedDateTo || undefined,
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export complete!');
    } catch (err) {
      toast.error('Failed to export invoices');
    }
  }, [selectedStatuses, filterCustomer, dueDateFrom, dueDateTo, issuedDateFrom, issuedDateTo]);

  const handlePaymentSuccess = useCallback(() => {
    setShowPaymentModal(false);
    setSelectedInvoice(null);
    toast.success(`Payment recorded for ${selectedInvoice?.invoice_number}`);
    fetchInvoices();
    fetchStats();
  }, [selectedInvoice, fetchInvoices, fetchStats]);

  const toggleStatus = useCallback((status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      }
      return [...prev, status];
    });
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedStatuses(['SENT']);
    setFilterCustomer('');
    setDueDateFrom('');
    setDueDateTo('');
    setIssuedDateFrom('');
    setIssuedDateTo('');
    setSearchQuery('');
    setPage(1);
  }, []);

  const hasFilters = useMemo(() => {
    return filterCustomer || dueDateFrom || dueDateTo || issuedDateFrom || issuedDateTo || searchQuery ||
      (selectedStatuses.length > 0 && !(selectedStatuses.length === 1 && selectedStatuses[0] === 'SENT'));
  }, [filterCustomer, dueDateFrom, dueDateTo, issuedDateFrom, issuedDateTo, searchQuery, selectedStatuses]);

  // ===== COLUMN DEFINITIONS =====
  
  const allColumns = useMemo(() => [
    {
      key: 'invoice_number',
      label: 'Invoice #',
      sortable: true,
      width: 140,
      render: (value) => (
        <span className="font-mono text-sm font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: 'order_number',
      label: 'Order #',
      sortable: true,
      width: 130,
      render: (value, row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          {value || row.order_id || '—'}
        </button>
      ),
    },
    {
      key: 'account_name',
      label: 'Customer',
      sortable: true,
      width: 200,
      render: (value) => (
        <span className="text-sm text-gray-700 truncate block max-w-[200px]" title={value}>
          {value || '—'}
        </span>
      ),
    },
    {
      key: 'issued_at',
      label: 'Issued',
      sortable: true,
      width: 110,
      render: (value) => (
        <span className="text-sm text-gray-600">{formatDate(value)}</span>
      ),
    },
    {
      key: 'due_at',
      label: 'Due Date',
      sortable: true,
      width: 110,
      render: (value) => (
        <span className="text-sm text-gray-600">{formatDate(value)}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: 140,
      render: (value, row) => (
        <StatusBadge 
          status={row.status_normalized || value} 
          isOverdue={row.is_overdue}
          daysOverdue={row.days_overdue}
        />
      ),
    },
    {
      key: 'days_overdue',
      label: 'Days Overdue',
      sortable: false,
      width: 100,
      align: 'center',
      render: (value, row) => (
        <DaysOverdueDisplay daysOverdue={value} isOverdue={row.is_overdue} />
      ),
    },
    {
      key: 'total',
      label: 'Total',
      sortable: true,
      width: 100,
      align: 'right',
      render: (value, row) => {
        const cents = row.total_cents || (typeof value === 'number' ? value * 100 : 0);
        return <span className="text-sm font-medium text-gray-900">{formatCurrency(cents)}</span>;
      },
    },
    {
      key: 'balance_due_cents',
      label: 'Balance Due',
      sortable: true,
      width: 110,
      align: 'right',
      render: (value) => (
        <span className={`text-sm font-medium ${value > 0 ? 'text-amber-600' : 'text-green-600'}`}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      key: 'amount_paid_cents',
      label: 'Paid',
      sortable: true,
      width: 100,
      align: 'right',
      render: (value) => (
        <span className="text-sm text-gray-600">{formatCurrency(value || 0)}</span>
      ),
    },
    {
      key: 'account_id',
      label: 'Account ID',
      sortable: false,
      width: 130,
      render: (value) => (
        <span className="font-mono text-xs text-gray-500">{value || '—'}</span>
      ),
    },
  ], [handleRowClick]);

  // Filter and order columns based on preferences
  const columns = useMemo(() => {
    return columnOrder
      .filter(key => visibleColumns[key])
      .map(key => allColumns.find(c => c.key === key))
      .filter(Boolean);
  }, [allColumns, visibleColumns, columnOrder]);

  // ===== RENDER =====
  
  return (
    <div className="space-y-4" data-testid="admin-invoices-page">
      {/* Header with Stats */}
      <AdminListHeader
        title="Invoices"
        subtitle="Accounts Receivable"
        onRefresh={handleRefresh}
        refreshing={loading}
        density={density}
        onDensityChange={setDensity}
        actions={
          <>
            <ColumnSelector
              columns={allColumns}
              visibleColumns={visibleColumns}
              onVisibilityChange={setVisibleColumns}
              columnOrder={columnOrder}
              onOrderChange={setColumnOrder}
            />
            <Button variant="outline" size="sm" onClick={handleExportCsv} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </>
        }
        data-testid="invoices-list-header"
      />
      
      {/* Stats Cards */}
      <StatsCards stats={stats} loading={statsLoading} />
      
      {/* Search + Filters */}
      <div className="space-y-3">
        {/* Global Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search invoices, orders, customers..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="pl-9"
              data-testid="invoice-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
        
        {/* Status Chips + Other Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status chips */}
          {STATUS_OPTIONS.map(status => {
            const isActive = selectedStatuses.includes(status.value);
            return (
              <Button
                key={status.value}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleStatus(status.value)}
                className={`text-xs ${isActive ? '' : 'text-gray-600'}`}
              >
                {status.label}
              </Button>
            );
          })}
          
          <div className="w-px h-6 bg-gray-200 mx-2" />
          
          {/* Customer filter */}
          <Select value={filterCustomer || 'all'} onValueChange={(v) => { setFilterCustomer(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[180px] h-8 text-sm">
              <Building2 className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              <SelectValue placeholder="All Customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Due Date filter (primary) */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                Due Date
                {(dueDateFrom || dueDateTo) && <Badge variant="secondary" className="ml-1.5 text-xs">Active</Badge>}
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3">
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">Due Date Range</label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={dueDateFrom}
                    onChange={(e) => { setDueDateFrom(e.target.value); setPage(1); }}
                    className="text-sm h-8"
                    placeholder="From"
                  />
                  <Input
                    type="date"
                    value={dueDateTo}
                    onChange={(e) => { setDueDateTo(e.target.value); setPage(1); }}
                    className="text-sm h-8"
                    placeholder="To"
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Issued Date filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                Issued Date
                {(issuedDateFrom || issuedDateTo) && <Badge variant="secondary" className="ml-1.5 text-xs">Active</Badge>}
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3">
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">Issued Date Range</label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={issuedDateFrom}
                    onChange={(e) => { setIssuedDateFrom(e.target.value); setPage(1); }}
                    className="text-sm h-8"
                    placeholder="From"
                  />
                  <Input
                    type="date"
                    value={issuedDateTo}
                    onChange={(e) => { setIssuedDateTo(e.target.value); setPage(1); }}
                    className="text-sm h-8"
                    placeholder="To"
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      
      {/* Data Table */}
      <AdminDataTable
        data={invoices}
        columns={columns}
        loading={loading}
        emptyMessage="No invoices found"
        emptyIcon={FileText}
        
        // Sorting
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        
        // Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        
        // Row behavior
        onRowClick={handleRowClick}
        getRowId={(row) => row.id || row.invoice_number}
        
        // Density
        density={density}
        
        // Row actions
        rowActions={(row) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Eye className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRowClick(row); }}>
                <Eye className="w-4 h-4 mr-2" />
                View Order
              </DropdownMenuItem>
              {row.balance_due_cents > 0 && (
                <>
                  <DropdownMenuItem onClick={(e) => handleRecordPayment(row, e)}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Record Payment
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => handleSendReminder(row, e)}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Reminder
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        
        data-testid="invoices-data-table"
      />
      
      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <CreatePaymentModal
          isOpen={showPaymentModal}
          onClose={() => { setShowPaymentModal(false); setSelectedInvoice(null); }}
          onSuccess={handlePaymentSuccess}
          prefilledCustomerId={selectedInvoice.account_id}
          prefilledCustomerName={selectedInvoice.account_name}
          prefilledInvoices={[selectedInvoice]}
          mode="ar"
        />
      )}
    </div>
  );
};

export default AdminInvoices;
